import React, { FC, useEffect } from 'react';
import { PoolSizeAdjustmentInput, PoolSizeAdjustmentReason } from 'shared';

import { CloseIcon } from '../../../assets/icons/index';
import { AlertDialogWrapper } from '../../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import CircleLoader from '../../../components/circle-loader/index';
import { AnimatedHeight } from '../../../components/motion/index';
import { Pool } from '../../../hooks/index';
import { toNumber } from '../../../utils/to-number.util';
import { adjustPoolSizeStore } from './adjust-pool-size.store';
import { DataEntryCorrectionStep } from './steps/data-entry-correction-step';
import { FormalAdjustmentStep } from './steps/formal-adjustment-step';
import { ReasonStep } from './steps/reason-step';

export type AdjustPoolSizeModalProps = {
  companyId: string;
};

export const AdjustPoolSizeModal: FC<AdjustPoolSizeModalProps> = ({ companyId }) => {
  const isOpen = adjustPoolSizeStore(({ isOpen }) => isOpen);
  const poolId = adjustPoolSizeStore(({ poolId }) => poolId);
  const reason = adjustPoolSizeStore(({ reason }) => reason);
  const canGoBack = adjustPoolSizeStore(({ canGoBack }) => canGoBack);
  const previousReason = adjustPoolSizeStore(({ previousReason }) => previousReason);
  const onStage = adjustPoolSizeStore(({ onStage }) => onStage);
  const stagedPoolSize = adjustPoolSizeStore(({ stagedPoolSize }) => stagedPoolSize);
  const close = adjustPoolSizeStore(({ close }) => close);
  const goBack = adjustPoolSizeStore(({ goBack }) => goBack);
  const reset = adjustPoolSizeStore(({ reset }) => reset);
  const selectReason = adjustPoolSizeStore(({ selectReason }) => selectReason);

  // The modal stays mounted with its page: leaving the page must not carry an open modal to the next
  // one (browser Back from pool details would reopen it on Pools & Plans).
  useEffect(() => reset, [reset]);

  // Closed, the modal keeps its last pool for the fade-out but must not refetch it: deleting that
  // pool would otherwise refetch a 404 and toast "Pool not found" over a delete that succeeded.
  const { pool } = Pool.usePool({ companyId, poolId: poolId ?? '', enabled: isOpen });
  const { adjustSize, isPending } = Pool.useAdjustSize({ onSuccess: close });

  const context = pool
    ? {
        // An adjustment staged in Edit Pool is not saved yet, so the next one starts from the size
        // it leaves. `dillutedSharesCount` is a bigint column and arrives as a string.
        currentPoolSize: stagedPoolSize ?? toNumber(pool.dillutedSharesCount),
        grantedShares: toNumber(pool.granted),
      }
    : null;

  const handleSubmit = (data: PoolSizeAdjustmentInput) => {
    // The step stays on screen while the modal fades out after a success; a click in that window
    // must not resize the pool a second time.
    if (!poolId || !isOpen) return;

    // From Edit Pool the adjustment waits for the blade's save, so nothing is sent yet.
    if (onStage) {
      onStage(data);
      close();
      return;
    }

    adjustSize({ companyId, poolId, data });
  };

  const handleBack = canGoBack ? goBack : undefined;

  // Names what the modal shows, so a change eases the modal to the new step's height.
  const stepKey = context ? (reason ?? 'reason') : 'loading';

  return (
    <AlertDialogWrapper control={{ onOpenChange: (open) => !open && close(), open: isOpen }}>
      <div className="flex w-full max-w-[500px] flex-col gap-4 rounded bg-white p-4 shadow-3xl">
        <div className="flex w-full items-start justify-between gap-4">
          <div className="flex min-w-0 flex-col">
            <span className="text-xl font-semibold text-gray-700">
              <AppFormattedMessage id="ADJUST_POOL_SIZE" />
            </span>
            <span className="truncate text-xs font-[450] text-gray-500">{pool?.name}</span>
          </div>
          <Button
            aria-label="Close"
            className="-mt-1 size-8 shrink-0 rounded transition-colors hover:bg-gray-100"
            onClick={close}
            styleType="NONE"
          >
            <CloseIcon className="size-[14px]" iconColor="#667085" />
          </Button>
        </div>

        <AnimatedHeight animationKey={stepKey}>
          {/* Keyed by step, so every step settles in as it replaces the previous one. */}
          <div
            className="flex w-full flex-col gap-4 duration-300 ease-out animate-in fade-in-0 slide-in-from-bottom-2 motion-reduce:animate-none"
            key={stepKey}
          >
            {!context ? (
              <div className="flex h-24 w-full items-center justify-center">
                <CircleLoader />
              </div>
            ) : reason === PoolSizeAdjustmentReason.FORMAL_ADJUSTMENT ? (
              <FormalAdjustmentStep
                context={context}
                isPending={isPending}
                onBack={handleBack}
                onCancel={close}
                onSubmit={handleSubmit}
              />
            ) : reason === PoolSizeAdjustmentReason.DATA_ENTRY_CORRECTION ? (
              <DataEntryCorrectionStep
                context={context}
                isPending={isPending}
                onBack={handleBack}
                onCancel={close}
                onSubmit={handleSubmit}
              />
            ) : (
              <ReasonStep
                initialReason={previousReason}
                onCancel={close}
                onContinue={selectReason}
              />
            )}
          </div>
        </AnimatedHeight>
      </div>
    </AlertDialogWrapper>
  );
};
